import React, { useRef, useState } from "react"
import styled, { css, useTheme } from "styled-components"
import { Icon } from "~/ur-react-components"
import { useClickOutside, useTranslate } from "~/ur-react-hooks"

interface LayoutProps {
  isOpen: boolean
  width?: string
}

const Layout = styled.div<LayoutProps>`
  position: relative;
  height: 48px;

  width: ${(props) => props.width};
  border-radius: 4px;
  box-sizing: border-box;
`

const Clear = styled.div`
  display: none;

  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  margin-top: 2px;
  margin-left: 1rem;

  &:hover {
    background-color: ${(props) => props.theme.colors.gray95};
  }
`

interface DisplayProps {
  isOpen: boolean
  width?: string
  hasError: boolean
  placeholderColor?: string
  isPlaceholderDisplayed: boolean
}
const Display = styled.div<DisplayProps>`
  display: flex;
  height: 48px;
  align-items: center;

  padding: 12px;
  box-sizing: border-box;

  border-radius: 4px;
  color: ${(props) => props.theme.colors.gray0};
  width: ${(props) => props.width};

  border: 1px solid ${(props) => props.theme.colors.gray70};

  ${(props) =>
    props.isPlaceholderDisplayed &&
    props.placeholderColor &&
    css`
      color: ${props.placeholderColor};
    `}

  ${(props) =>
    props.isOpen &&
    css`
      border: 2px solid ${props.theme.colors.primary};
      padding-left: 11px;
      padding-bottom: 14px;

      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
      border-bottom: none;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border: 2px solid ${props.theme.colors.error40};
      padding-left: 11px;
      padding-bottom: 14px;
    `}

  &:hover {
    ${Clear} {
      display: block;
    }
  }
`

const ChevronWrapper = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  padding: 1rem;
`

interface OptionsProps {
  isOpen: boolean
  width?: string
  hasError: boolean
}

const Options = styled.div<OptionsProps>`
  display: ${(props) => (props.isOpen ? "block" : "none")};

  position: absolute;
  z-index: 4000;
  top: 46px;
  left: 0;
  right: 0;

  margin: 0;
  padding: 0;
  box-sizing: border-box;

  box-shadow: ${(props) => props.theme.shadow.primary};

  list-style: none;
  background: white;
  border-radius: 0 0 4px 4px;

  ${(props) =>
    props.isOpen &&
    css`
      border: 2px solid ${props.theme.colors.primary};

      border-top-right-radius: 0;
      border-top-left-radius: 0;
      border-top: none;
    `}

  ${(props) =>
    props.hasError &&
    css`
      border: 2px solid ${props.theme.colors.error40};
      border-top: none;
    `}
`

const OptionLabel = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 12px;
  box-sizing: border-box;
  gap: 0.5rem;

  height: 48px;
  color: ${(props) => props.theme.colors.gray30};

  &:hover {
    background: ${(props) => props.theme.colors.primary95};
  }
`

const OptionIconWrapper = styled.div`
  line-height: 0;
`

const ClearOptionLabel = styled(OptionLabel)`
  color: ${(props) => props.theme.colors.gray50};
  justify-content: space-between;
`

const DisplayOption = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 12px;
  padding-left: 1px;
  box-sizing: border-box;
  width: 100%;
  gap: 0.5rem;

  height: 48px;
  color: ${(props) => props.theme.colors.gray30};
`

const Option = styled.li`
  ${OptionLabel}:last-of-type {
    border-radius: 0 0 8px 8px;
  }
`

const Error = styled.div`
  color: ${(props) => props.theme.colors.error40};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;
`

const Warning = styled.div`
  color: ${(props) => props.theme.colors.warning40};
  font-size: 12px;
  margin-top: 4px;
  margin-left: 4px;

  position: absolute;
`

export type AllowedOptionValueType = string | number | null

export interface SelectOption<
  ValueType extends AllowedOptionValueType,
  ExtraType = any,
> {
  value: ValueType
  label: string | React.ReactNode
  icon?: React.ReactNode | string
  extra?: ExtraType
}

export interface SelectProps<
  ValueType extends AllowedOptionValueType,
  ExtraDataType = any,
> {
  value: ValueType
  onChange: (newValue: ValueType, extraData?: ExtraDataType) => void
  options: SelectOption<ValueType, ExtraDataType>[]
  clearableValue?: ValueType

  width?: string
  placeholder?: string
  placeholderColor?: string

  error?: string | null
  warning?: string | null
}

export const Select = <
  ValueType extends AllowedOptionValueType,
  ExtraDataType,
>({
  value,
  onChange,
  options,
  width,
  placeholder: _placeholder,
  placeholderColor: _placeholderColor,
  clearableValue,
  error,
  warning,
}: SelectProps<ValueType, ExtraDataType>) => {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef(null)
  const theme = useTheme()

  const translations = useTranslate({
    defaultPlaceholder: "common.select-placeholder",
    clear: "common.clear",
  })

  useClickOutside(ref, () => {
    setIsOpen(false)
  })

  const placeholder = _placeholder || translations.defaultPlaceholder
  const selectedOption = options.find((option) => value === option.value)
  const valueDisplay = selectedOption ? selectedOption.label : placeholder
  const placeholderColor = _placeholderColor || theme.colors.gray50
  const isPlaceholderDisplayed = value === clearableValue

  return (
    <Layout ref={ref} isOpen={isOpen} width={width}>
      <Display
        isOpen={isOpen}
        width={width}
        onClick={() => setIsOpen(!isOpen)}
        hasError={!!error}
        isPlaceholderDisplayed={isPlaceholderDisplayed}
        placeholderColor={placeholderColor}
      >
        {isOpen ? (
          <DisplayOption>
            {selectedOption?.icon && (
              <OptionIconWrapper>{selectedOption.icon}</OptionIconWrapper>
            )}
            {valueDisplay}
            <ChevronWrapper>
              <Icon icon="chevron-up" size="16px" color={theme.colors.gray50} />
            </ChevronWrapper>
          </DisplayOption>
        ) : (
          <DisplayOption>
            {selectedOption?.icon && (
              <OptionIconWrapper>{selectedOption.icon}</OptionIconWrapper>
            )}
            {valueDisplay}
            <ChevronWrapper>
              <Icon
                icon="chevron-down"
                size="16px"
                color={theme.colors.gray50}
              />
            </ChevronWrapper>
          </DisplayOption>
        )}
      </Display>
      {error && <Error>{error}</Error>}
      {warning && <Warning>{warning}</Warning>}
      <Options isOpen={isOpen} width={width} hasError={!!error}>
        {clearableValue !== undefined && (
          <Option
            onClick={() => {
              onChange(clearableValue)
              setIsOpen(false)
            }}
          >
            <ClearOptionLabel>
              {translations.clear}
              <Icon icon="trash" />
            </ClearOptionLabel>
          </Option>
        )}
        {options
          .filter((option) => option.value !== value)
          .map((option) => (
            <Option
              key={option.value}
              onClick={() => {
                onChange(option.value, option.extra)
                setIsOpen(false)
              }}
            >
              <OptionLabel>
                {option.icon && (
                  <OptionIconWrapper>{option.icon}</OptionIconWrapper>
                )}
                {option.label}
              </OptionLabel>
            </Option>
          ))}
      </Options>
    </Layout>
  )
}

export default Select
